import { useParams, Link } from 'react-router-dom';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  useScrollToTop();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 max-w-7xl mx-auto text-center py-20">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，你查找的文章不存在或已被移除。</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.category, href: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '蜜桃网站',
      logo: {
        '@type': 'ImageObject',
        url: 'https://mitao.readdy.ai/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mitao.readdy.ai/article/${article.slug}`,
    },
  };

  const categoryInfo = categories.find((c) => c.slug === article.categorySlug);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 mb-6 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-3">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">蜜桃网站签约作者</p>
                </div>
              </div>
            </header>

            <figure className="mb-10 -mx-4 md:mx-0">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-[280px] md:h-[400px] object-cover object-top rounded-none md:rounded-lg"
              />
            </figure>

            <div className="prose-custom max-w-none mb-12">
              {article.content.map((paragraph, index) => (
                <p key={index} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200/60">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-4 border-t border-background-200/60 mb-10">
              <Link
                to={`/category/${article.categorySlug}`}
                className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                返回{article.category}
              </Link>
              <div className="flex gap-2">
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-lg w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mb-12">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.id} article={related} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}